import Theme from "../components/Theme";
import dynamic from "next/dynamic";
import { useEffect } from "react";
import AdaptiveCriticalImage from "../components/atoms/AdaptiveCriticalImage";
import FeaturesShowCaseCards from "../components/organisms/features/FeaturesShowCaseCards";
import WideCTA from "../components/organisms/common/WideCTA";
import TpWidget from "../components/atoms/document/TpWidget";
import Scroll from "../components/organisms/enterprise/Scroll";
import EnterpriseForm from "../components/organisms/common/forms/EnterpriseForm";
import BadgeCarousel from "../components/organisms/common/BadgeCarousel";
const TpWidgetMicro = dynamic(() =>
  import("../components/organisms/common/TpWidgetMicro")
);
const BoxShadowCard = dynamic(() =>
  import("../components/organisms/common/BoxShadowCard")
);
const FeaturedImage = dynamic(() =>
  import("../components/organisms/common/FeaturedImage")
);
const People = dynamic(() => import("../components/organisms/common/People"));
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../styles/cssinjs/helpers/variables.js";
import { carouselAction } from "../util/utils";
// Local Components
const Hero = ({ data }) => {
  return (
    <section className="container grid--default has-align-top hero--default">
      <article className="text-left">
        <h1 className="text-xl lg:pt-9">{data.title}</h1>
        <p className="lg:maxw-md lg:mb-5 md:mb-5">{data.desc}</p>
        <Scroll data={data.cta} />
        <TpWidgetMicro className="lg:mt-5 md:mt-3 md:pt-2" />
      </article>
      <figure className="lg:maxw-lg lg:ml-2 md:mt-4">
        <picture>
          <AdaptiveCriticalImage
            src={data.images.src}
            alt={data.images.alt}
            mobile={data.images.prefixes.mobile}
            dpi={data.images.prefixes.dpi}
            className="lg:w-auto img-fit"
            dimensions={data.images.dimensions}
          />
        </picture>
      </figure>
    </section>
  );
};

const SecondFold = ({ data }) => {
  return (
    <section className="highlight bg-light lg:pb-10 lg:pt-10 md:pb-10">
      <div className="container fx md:pt-5">
        <figure>
          <video
            autoPlay
            muted
            loop
            poster={data.image.src}
            preload="none"
            playsInline
            className="br-8 lg:w-auto md:-mb-5 md:mt-5 md:w-100"
          >
            <source src={data.image.src} type="video/mp4" />
          </video>
        </figure>
        <article className="lg:maxw-md lg:ml-auto lg:mr-0">
          <h2
            className="text-lg lg:mt-2 lg:mb-2.5 md:mt-2.5 md:mb-2"
            dangerouslySetInnerHTML={{ __html: data.title }}
          />
          <p className="lg:mb-2 md:mb-2">{data.desc[0]}</p>
          <p
            className="mb-5"
            dangerouslySetInnerHTML={{ __html: data.desc[1] }}
          />
          <a href={data.cta.href} className="btn-secondary lg:px-9 md:w-100">
            <span>{data.cta.text}</span>
          </a>
        </article>
      </div>
    </section>
  );
};

const FeaturesShowCaseCard = ({ data }) => {
  return (
    <section className="bg-offset-top lg:pb-15 lg:pt-5 md:pt-10 md:pb-10">
      <article className="container lg:maxw-xl lg:text-center lg:ml-auto lg:mr-auto lg:pb-10">
        <h2 className="lg:maxw-xl mx-auto lg:mb-3 md:mb-2 text-lg">
          {data.title}
        </h2>
        <p className="lg:maxw-xls mx-auto md:mb-3">{data.desc}</p>
      </article>
      <FeaturesShowCaseCards data={data.featuresShowcaseCards} />
    </section>
  );
};

const CTAWithLogo = ({ data }) => {
  return (
    <section className="industry-masonry">
      <article className="bg-light br-8 container lg:pb-10 lg:pt-10 md:pb-8 md:pt-5">
        <h2 className="container lg:mb-8 md:mb-10 md:ml-9 md:mr-10 people-love-setmore text-center text-xl">
          <span>{data.heading[0]}</span>
          <span className="heart-glyph lg:ml-1 lg:mr-1">❤️</span>
          <span>{data.heading[1]}</span>
        </h2>
        <ul className="container fx--sb lg:d-fx lg:maxw-xls lg:ml-auto lg:mr-auto text-center">
          <li className="md:mb-10">
            <img
              src={data.image[0].src}
              alt={data.image[0].alt}
              loading="lazy"
            />
          </li>
          <li className="md:mb-10">
            <img
              src={data.image[1].src}
              alt={data.image[1].alt}
              loading="lazy"
            />
          </li>
          <li>
            <img
              src={data.image[2].src}
              alt={data.image[2].alt}
              loading="lazy"
            />
          </li>
        </ul>
      </article>
    </section>
  );
};
const CTA = ({ data, reqCountry }) => {
  return (
    <section
      className="lg:mt-20 lg:mb-20 md:mt-10 md:mb-10 mission-cta"
      id="form-view"
    >
      <div className="br-8 container cta-contrast has-image lg:d-fx lg:mb-20 lg:pb-10 lg:pt-10 md:ml-0 md:mr-0 md:mb-10">
        <article className="lg:maxw-lg lg:pr-10 md:mb-5">
          <h2
            className="lg:mb-3 md:mb-2"
            dangerouslySetInnerHTML={{ __html: data.heading }}
          />
          <p dangerouslySetInnerHTML={{ __html: data.desc }} />
        </article>
        <div className="form-holder form-container">
          <EnterpriseForm reqCountry={reqCountry} />
        </div>
      </div>
    </section>
  );
};

const BadgeNavBoxSection = () => {
  return (
    <section className="lg:mb-20 md:mb-9">
      <h2 className="text-lg text-center mx-1">Performance like no other</h2>
      <style jsx>{`
        h2 {
          font-size: ${font.size.lg[2]};
          line-height: ${font.size.lg[3]};
        }
      `}</style>
      <BadgeCarousel className="mt-4" />
    </section>
  );
};

export const BoxShadowCardSection = ({ data }) => {
  const appMode = process.env.APP_MODE;
  const link = data?.href?.[appMode] || data?.href;
  return (
    <section className="industry-features bg-offset bg-offset--adapt md:pb-10 lg:pb-15">
      <article className="container text-center lg:maxw-2xl lg:ml-auto lg:mr-auto lg:mb-9 md:mb-5">
        <h2
          className="lg:mb-2 md:text-mds md:mb-1.5"
          dangerouslySetInnerHTML={{ __html: data.title }}
        />
        <p className="lg:maxw-md mx-auto">{data.desc}</p>
      </article>
      <BoxShadowCard data={data.list} />
      <div className="container mt-5 text-center">
        <a
          href={link}
          className="btn-primary lg:px-10 md:w-100"
          data-testid="cta-link"
        >
          <span>{data.cta}</span>
        </a>
      </div>
    </section>
  );
};
export async function getServerSideProps({ req, locale }) {
  let allData = {};
  const baseUrl = `${req.headers["x-forwarded-proto"]}://${req.headers.host}`;
  let getCountry = await fetch(`${baseUrl}/api/getCountry`);
  getCountry = await getCountry.json();
  const reqCountry = getCountry.countryCode.toLowerCase();

  // Fetch
  const headerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/header.json`
  );

  const footerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/footer.json`
  );

  const marketingResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/others/enterprise/marketing.json`
  );
  const contentResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/others/enterprise/content.json`
  );

  // Catch Error
  if (
    !headerResponse.ok ||
    !footerResponse.ok ||
    !marketingResponse.ok ||
    !contentResponse.ok
  ) {
    return {
      notFound: true,
    };
  }

  // Convert
  allData.header = await headerResponse.json();
  allData.footer = await footerResponse.json();
  allData.marketing = await marketingResponse.json();
  allData.content = await contentResponse.json();

  // Wrap
  const header = {
    header: allData.header,
  };
  const footer = {
    footer: allData.footer,
  };

  // Combine all data
  const data = await {
    ...header,
    ...footer,
    ...allData.marketing,
    ...allData.content,
  };

  return {
    props: {
      data,
      reqCountry: reqCountry,
    },
  };
}

export default function Enterprise({ data, reqCountry }) {
  const { header, footer, seo, content } = data;

  useEffect(() => {
    carouselAction();
  }, []);
  return (
    <>
      <Theme
        header={header}
        footer={footer}
        className="industry-page-with-trust-pilot lander-enterprise cls-improvement--desktop"
        seo={seo}
      >
        <Hero data={content.hero} />
        <SecondFold data={content.cutoutFeaturedImage} />
        <FeaturesShowCaseCard data={content.featuresShowcaseHeader} />
        <CTAWithLogo data={content.ctaWithLogo} />
        <section className="lg:mt-20 md:mt-10 md:mb-10">
          {content.featuredImageSectionsOne.map((item, index) => {
            return (
              <FeaturedImage
                key={index}
                data={item}
                imageAlign={item.image.align}
              />
            );
          })}
        </section>
        <CTA data={content.cta} reqCountry={reqCountry} />
        <section className="lg:mb-20 md:mb-10">
          {content.featuredImageSectionsTwo.map((item, index) => {
            return (
              <FeaturedImage
                key={index}
                data={item}
                imageAlign={item.image.align}
              />
            );
          })}
        </section>
        <BadgeNavBoxSection />
        <BoxShadowCardSection data={content.industrySpecificFeatures} />
        <People className="bg-light" />
        <WideCTA data={content.wideCTA} />
        <TpWidget />
      </Theme>
    </>
  );
}
